import { gsap } from 'gsap'
import GoldSweep from '../particles/GoldSweep'

/**
 * ThreeController
 *
 * Three.js has been removed entirely — the bottle is gone and GoldSweep
 * is pure canvas-2D (no WebGL dependency).
 *
 * What stays:
 *  - GoldSweep wrapper  (createParticles / smokeAnimation)
 *  - Loading screen logic  (session storage, progress bar, gsap fade)
 *
 * What was removed:
 *  - THREE, OrbitControls, FontLoader  imports
 *  - WebGL renderer, scene, camera, lights, canvas.webgl
 *  - PerfumeBottle import and addPerfumeBottle()
 *  - 60fps tick() render loop
 *  - Shadow map pass  (was enabled but nothing cast shadows)
 *  - powerPreference: 'high-performance'  (drained mobile battery)
 *
 * removeCurrentPerfumeBottle() and addPerfumeBottle() are kept as no-ops
 * so every existing call-site in script.js and CatalogueController works
 * without any changes.
 */
export default class ThreeController {

    constructor() {
        this.particles = null

        // Session-based loader: first visit = cinematic, returning = fast
        this.hasVisited          = sessionStorage.getItem('siteLoaded')
        this.minimumLoadingTime  = this.hasVisited ? 350 : 1800
        this.actualProgress      = 0
        this.displayedProgress   = 0
        this.loadingComplete     = false
        this.loadingStartTime    = performance.now()

        this._init()
    }

    _init() {
        if (this.hasVisited) {
            this._setActualProgress(100)
            setTimeout(() => this._completeLoadingScreen(), 250)
        } else {
            this._trackPageAssets()
        }
    }

    // ─────────────────────────────────────────────────────
    // PARTICLES  (GoldSweep — canvas-2D, no WebGL)
    // ─────────────────────────────────────────────────────

    createParticles() {
        this.particles = new GoldSweep()
    }

    smokeAnimation() {
        if (this.particles) {
            this.particles.smokeAnimation()
        }
    }

    // ─────────────────────────────────────────────────────
    // BOTTLE STUBS  (bottle removed — kept for call-site compat)
    // ─────────────────────────────────────────────────────

    addPerfumeBottle()          {}
    removeCurrentPerfumeBottle() {}

    // ─────────────────────────────────────────────────────
    // LOADING SCREEN  (unchanged from original)
    // ─────────────────────────────────────────────────────

    _setActualProgress(value) {
        this.actualProgress = Math.min(100, value)
    }

    _animateLoadingBar() {
        const animate = () => {
            this.displayedProgress +=
                (this.actualProgress - this.displayedProgress) * 0.06

            const rounded = Math.round(this.displayedProgress)
            const bar     = document.getElementById('loading-bar')
            const pct     = document.getElementById('loading-percentage')

            if (bar) bar.style.width     = rounded + '%'
            if (pct) pct.textContent     = rounded + '%'

            if (!this.loadingComplete || this.displayedProgress < 99.5) {
                requestAnimationFrame(animate)
            }
        }
        animate()
    }

    _trackPageAssets() {
        const self = this

        // ── Hard caps — loading screen NEVER exceeds these ────────────────
        // On mobile (3G islands) we stay generous but still cap at 2.5s.
        // On desktop even a slow connection rarely needs more than 1.5s for
        // fonts. Product images are NOT tracked — they load progressively
        // after the screen clears, which is normal browser behaviour.
        const HARD_CAP_MS  = window.innerWidth < 768 ? 2500 : 1500

        const startTime    = performance.now()
        let fontsReady     = false
        let completed      = false

        // ── Time-based progress — bar always moves, never stuck at 0% ────
        // Formula: time fraction (up to 80%) + font bonus (20%)
        // This means even on 3G the bar visibly advances every tick.
        const calcPct = () => {
            const elapsed  = performance.now() - startTime
            const timeFrac = Math.min(0.80, elapsed / HARD_CAP_MS)
            const fontFrac = fontsReady ? 0.20 : 0
            return Math.min(99, (timeFrac + fontFrac) * 100)
        }

        // Drive the displayed bar forward every 80ms — no asset dependency
        const ticker = setInterval(() => {
            self._setActualProgress(calcPct())
        }, 80)

        this._animateLoadingBar()

        const complete = () => {
            if (completed) return
            completed = true
            clearInterval(ticker)
            self._setActualProgress(100)
            self._completeLoadingScreen()
        }

        // ── Fonts: the only real dependency ──────────────────────────────
        // Fonts affect every text element above the fold.
        // We race against 1 second so a slow CDN never blocks us.
        Promise.race([
            document.fonts.ready,
            new Promise(resolve => setTimeout(resolve, 1000))
        ]).then(() => {
            fontsReady = true
            self._setActualProgress(calcPct())

            // Fonts resolved — wait just long enough for the bar to
            // animate from its current position to ~95% before completing.
            // Minimum 300ms so the animation looks intentional, not a flash.
            const elapsed   = performance.now() - startTime
            const remaining = Math.max(300, HARD_CAP_MS * 0.55 - elapsed)
            setTimeout(complete, remaining)
        })

        // ── Absolute hard cap ─────────────────────────────────────────────
        // Fires unconditionally. Cannot be delayed by rAF competition,
        // slow assets, or anything else. This is the 3G safety net.
        setTimeout(complete, HARD_CAP_MS)
    }

    _completeLoadingScreen() {
        if (this.loadingComplete) return
        this.loadingComplete = true
        sessionStorage.setItem('siteLoaded', 'true')

        const screen = document.getElementById('loading-screen')
        if (!screen || screen.style.display === 'none') return

        screen.style.pointerEvents = 'none'
        gsap.to(screen, {
            opacity:    0,
            duration:   0.85,
            ease:       'power2.inOut',
            onComplete: () => { screen.style.display = 'none' }
        })
    }
}